import { lazy } from 'react';
import { RouteObject, Navigate, useParams } from 'react-router-dom';
import { SmartRedirect } from './SmartRedirect';
import CommissionsSwitch from './CommissionsSwitch';

// Preserves the dynamic `:id` param when redirecting legacy `/training/paths/:id`
// URLs to the new `/learning/paths/:id` detail page. A plain <Navigate to="..."/>
// drops the param, which is why the old redirect landed on the list page.
function LegacyTrainingPathRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? `/learning/paths/${id}` : '/learning/paths'} replace />;
}

// Lazy load pages
const DashboardPageEnhanced = lazy(() => import('../pages/DashboardPageEnhanced').then(m => ({ default: m.DashboardPageEnhanced })));
const DashboardGeneralPage = lazy(() => import('../pages/DashboardGeneralPage'));
const AdvisorDashboardPage = lazy(() => import('../modules/advisor-dashboard/pages/AdvisorDashboardPage'));
const DashboardAsesor2Page = lazy(() => import('../modules/dashboard/pages/DashboardAsesor2Page'));
const PanelSeguimientoPage = lazy(() => import('../modules/dashboard/pages/PanelSeguimientoPage'));
const TasksPage = lazy(() => import('../modules/tasks/pages/TasksPage'));
const ForgotPasswordPage = lazy(() => import('../pages/ForgotPassword'));
const ResetPasswordPage = lazy(() => import('../pages/ResetPassword'));
const CrmChangePasswordPage = lazy(() => import('../pages/CrmChangePasswordPage'));
const LeadsPage = lazy(() => import('../pages/LeadsPage').then(m => ({ default: m.LeadsPage })));
const PersonasPage = lazy(() => import('../pages/PersonasPage').then(m => ({ default: m.PersonasPage })));
const ClientsPage = lazy(() => import('../pages/ClientsPage').then(m => ({ default: m.ClientsPage })));
const ClientDetailPage = lazy(() => import('../pages/ClientDetailPage').then(m => ({ default: m.ClientDetailPage })));
const ClientChurnPage = lazy(() => import('../pages/ClientChurnPage').then(m => ({ default: m.ClientChurnPage })));
const ChatBotConfigPage = lazy(() => import('../pages/ChatBotConfigPage').then(m => ({ default: m.ChatBotConfigPage })));
const CalendarPage = lazy(() => import('../pages/CalendarPage').then(m => ({ default: m.CalendarPage })));
const TestPage = lazy(() => import('../pages/TestPage').then(m => ({ default: m.TestPage })));
const AIConfigPage = lazy(() => import('../pages/AIConfigPage').then(m => ({ default: m.AIConfigPage })));
const LeadDetailPageWrapper = lazy(() => import('../pages/LeadDetailPageWrapper').then(m => ({ default: m.LeadDetailPageWrapper })));
const LeadScoringPage = lazy(() => import('../pages/LeadScoringPage').then(m => ({ default: m.LeadScoringPage })));
const Score3DPage = lazy(() => import('../pages/Score3DPage').then(m => ({ default: m.Score3DPage })));
const BuyerPersonaPage = lazy(() => import('../modules/buyer-persona').then(m => ({ default: m.BuyerPersonaPage })));
const AICenterPage = lazy(() => import('../pages/AICenterPage').then(m => ({ default: m.AICenterPage })));
const AIConversationsPage = lazy(() => import('../pages/AIConversationsPage').then(m => ({ default: m.AIConversationsPage })));
const CallTestPage = lazy(() => import('../modules/omnichannel/pages/CallTestPage'));
const VideoCallTestPage = lazy(() => import('../modules/omnichannel/pages/VideoCallTestPage'));
const MeetingTestPage = lazy(() => import('../modules/omnichannel/pages/MeetingTestPage'));
const OmnichannelPage = lazy(() => import('../modules/omnichannel/pages/OmnichannelPage'));
const TeamChatPage = lazy(() => import('../modules/omnichannel/pages/TeamChatPage'));
const OmnichannelDashboardPage = lazy(() => import('../pages/OmnichannelDashboardPage'));
const DocsViewer = lazy(() => import('../modules/docs').then(m => ({ default: m.DocsViewer })));
const TeamDashboard = lazy(() => import('../modules/team').then(m => ({ default: m.TeamDashboard })));

// Role-based Dashboard Components
const AgentDashboard = lazy(() => import('../modules/dashboard/components/AgentDashboard').then(m => ({ default: m.AgentDashboard })));
const TeamDashboardModule = lazy(() => import('../modules/dashboard/components/TeamDashboard').then(m => ({ default: m.TeamDashboard })));
const ExecutiveDashboard = lazy(() => import('../modules/dashboard/components/executive/ExecutiveDashboard').then(m => ({ default: m.ExecutiveDashboard })));
const PresidentDashboard = lazy(() => import('../modules/dashboard/components/PresidentDashboard').then(m => ({ default: m.PresidentDashboard })));
const BusinessValueDashboard = lazy(() => import('../modules/dashboard/components/business-value/BusinessValueDashboard').then(m => ({ default: m.BusinessValueDashboard })));
const MarketingDashboardPage = lazy(() => import('../modules/marketing-dashboard').then(m => ({ default: m.MarketingDashboardPage })));
const CampaignDetailPage = lazy(() => import('../modules/marketing-dashboard/pages/CampaignDetailPage'));
const CustomDashboardsPage = lazy(() => import('../modules/custom-dashboards/pages/CustomDashboardsPage'));

// OwlySales v2 Dashboards
const ComandoCentralPage = lazy(() => import('../modules/owlysales/pages/ComandoCentralPage'));
const MisVentasPage = lazy(() => import('../modules/owlysales/pages/MisVentasPage'));
const AdquisicionPage = lazy(() => import('../modules/owlysales/pages/AdquisicionPage'));
const InventarioPage = lazy(() => import('../modules/owlysales/pages/InventarioPage'));
const EquipoOpsPage = lazy(() => import('../modules/owlysales/pages/EquipoOpsPage'));

const TeamCommissionsPage = lazy(() => import('../modules/team/pages/TeamCommissionsPage'));
const CommissionPlansPage = lazy(() => import('../modules/commissions/pages/CommissionPlansPage'));
const AdvisorCommissionsPage = lazy(() => import('../modules/commissions/pages/AdvisorCommissionsPage'));
const LeaderOverridesPage = lazy(() => import('../modules/commissions/pages/LeaderOverridesPage'));
const CommissionAnalyticsPage = lazy(() => import('../modules/commissions/pages/CommissionAnalyticsPage'));
const ClientDashboard = lazy(() => import('../pages/ClientDashboard').then(m => ({ default: m.ClientDashboard })));
const ClientScoringPage = lazy(() => import('../pages/ClientScoringPage').then(m => ({ default: m.ClientScoringPage })));
const VirtualScrollDemo = lazy(() => import('../pages/VirtualScrollDemo').then(m => ({ default: m.VirtualScrollDemo })));
const UnderConstructionPage = lazy(() => import('../pages/UnderConstructionPage').then(m => ({ default: m.UnderConstructionPage })));
const SalesBudgetPage = lazy(() => import('../modules/sales-budget').then(m => ({ default: m.SalesBudgetPage })));
const BadgeMigrationTestPage = lazy(() => import('../pages/BadgeMigrationTestPage'));
const DesignSystemPage = lazy(() => import('../modules/docs/design-system-2.0/pages/DesignSystemPage').then(m => ({ default: m.DesignSystemPage })));
const TaskAutomationPage = lazy(() => import('../modules/task-automation/pages/TaskAutomationPage').then(m => ({ default: m.TaskAutomationPage })));
// VERSIONING REMOVED - Single automation per project
// const AutomationVersionsPage = lazy(() => import('../modules/task-automation/pages/AutomationVersionsPage').then(m => ({ default: m.AutomationVersionsPage })));
const AutomationsListPage = lazy(() => import('../modules/task-automation/pages/AutomationsListPage').then(m => ({ default: m.AutomationsListPage })));
const AutomationActiveRouter = lazy(() => import('../modules/task-automation/pages/AutomationActiveRouter').then(m => ({ default: m.AutomationActiveRouter })));
const ExitReasonsPage = lazy(() => import('../pages/ExitReasonsPage').then(m => ({ default: m.ExitReasonsPage })));
const TransferReasonsPage = lazy(() => import('../pages/TransferReasonsPage').then(m => ({ default: m.TransferReasonsPage })));
const LeadSourcesPage = lazy(() => import('../pages/LeadSourcesPage').then(m => ({ default: m.LeadSourcesPage })));
const LeadFunnelPage = lazy(() => import('../pages/LeadFunnelPage').then(m => ({ default: m.LeadFunnelPage })));
const LeadPlaybookPage = lazy(() => import('../pages/LeadPlaybookPage').then(m => ({ default: m.LeadPlaybookPage })));
const ClientFunnelPage = lazy(() => import('../pages/ClientFunnelPage').then(m => ({ default: m.ClientFunnelPage })));
const PublicSiteDemo = lazy(() => import('../modules/public-site/pages/PublicSiteDemo').then(m => ({ default: m.PublicSiteDemo })));
const OrbitViewPage = lazy(() => import('../modules/public-site/pages/OrbitViewPage').then(m => ({ default: m.OrbitViewPage })));
const ShowcasePage = lazy(() => import('../modules/public-site/pages/ShowcasePage').then(m => ({ default: m.ShowcasePage })));
const SetupEmailDomainPage = lazy(() => import('../modules/integrations/pages/SetupEmailDomainPage').then(m => ({ default: m.SetupEmailDomainPage })));
const ScoringOrbitViewPage = lazy(() => import('../modules/voice-agent/pages/ScoringOrbitViewPage').then(m => ({ default: m.ScoringOrbitViewPage })));
// Lazy loading para TODAS las páginas (incluyendo públicas) para mejorar performance
const QuotePublicPage = lazy(() => import('../modules/quotes/pages/QuotePublicPage'));
const LoginPage = lazy(() => import('../pages/Login'));
const ApiKeysPage = lazy(() => import('../modules/api-keys').then(m => ({ default: m.ApiKeysPage })));


// Portal de Clientes - Pages
const PortalApp = lazy(() => import('../modules/portal/components/PortalApp').then(m => ({ default: m.PortalApp })));
const PortalLoginPage = lazy(() => import('../modules/portal/pages/PortalLoginPage').then(m => ({ default: m.PortalLoginPage })));
const PortalChangePasswordPage = lazy(() => import('../modules/portal/pages/PortalChangePasswordPage').then(m => ({ default: m.PortalChangePasswordPage })));
const PortalForgotPasswordPage = lazy(() => import('../modules/portal/pages/PortalForgotPasswordPage').then(m => ({ default: m.PortalForgotPasswordPage })));
const PortalResetPasswordPage = lazy(() => import('../modules/portal/pages/PortalResetPasswordPage').then(m => ({ default: m.PortalResetPasswordPage })));
const PortalDashboardPage = lazy(() => import('../modules/portal/pages/PortalDashboardPage').then(m => ({ default: m.PortalDashboardPage })));
const PortalPropertiesPage = lazy(() => import('../modules/portal/pages/PortalPropertiesPage').then(m => ({ default: m.PortalPropertiesPage })));
const PortalPropertyDetailPage = lazy(() => import('../modules/portal/pages/PortalPropertyDetailPage').then(m => ({ default: m.PortalPropertyDetailPage })));
const PortalPaymentSchedulePage = lazy(() => import('../modules/portal/pages/PortalPaymentSchedulePage').then(m => ({ default: m.PortalPaymentSchedulePage })));
const QuoteEditPage = lazy(() => import('../modules/quotes/pages/QuoteEditPage'));
const QuoteVersionDemo = lazy(() => import('../modules/quotes/pages/QuoteVersionDemo'));
const QuotesPage = lazy(() => import('../pages/QuotesPage').then(m => ({ default: m.QuotesPage })));
const QuoteSettingsPage = lazy(() => import('../pages/QuoteSettingsPage').then(m => ({ default: m.QuoteSettingsPage })));
const MLInsightsPage = lazy(() => import('../pages/MLInsightsPage').then(m => ({ default: m.MLInsightsPage })));

// Monitoring Pages
const MonitoringDashboardPage = lazy(() => import('../pages/monitoring').then(m => ({ default: m.MonitoringDashboardPage })));
const PerformanceMonitorPage = lazy(() => import('../pages/monitoring').then(m => ({ default: m.PerformanceMonitorPage })));
const ErrorMonitorPage = lazy(() => import('../pages/monitoring').then(m => ({ default: m.ErrorMonitorPage })));

// Users Module Pages
const UsersListPage = lazy(() => import('../modules/users/pages/UsersListPage'));
const UserDetailPageModule = lazy(() => import('../modules/users/pages/UserDetailPage'));
const UserCreatePage = lazy(() => import('../modules/users/pages/UserCreatePage'));
const UserEditPage = lazy(() => import('../modules/users/pages/UserEditPage'));
const RolesPage = lazy(() => import('../modules/users/pages/roles/RolesPage'));
const RoleCreatePage = lazy(() => import('../modules/users/pages/roles/RoleCreatePage'));
const RolesPermissionsPageV2 = lazy(() => import('../modules/users/pages/permissions/RolesPermissionsPageV2'));
const PermissionsMatrixPage = lazy(() => import('../modules/users/pages/permissions/PermissionsMatrixPage'));
const DepartmentsPageUsers = lazy(() => import('../modules/users/pages/DepartmentsPage'));

// Financial Pages
const FinancialDashboardPage = lazy(() => import('../pages/FinancialDashboardPage').then(m => ({ default: m.FinancialDashboardPage })));
const IncomesPage = lazy(() => import('../pages/IncomesPage').then(m => ({ default: m.IncomesPage })));
const CostsPage = lazy(() => import('../pages/CostsPage').then(m => ({ default: m.CostsPage })));
const BudgetsPage = lazy(() => import('../pages/BudgetsPage').then(m => ({ default: m.BudgetsPage })));
const IncomeCategoriesPage = lazy(() => import('../pages/IncomeCategoriesPage').then(m => ({ default: m.IncomeCategoriesPage })));
const CostCategoriesPage = lazy(() => import('../pages/CostCategoriesPage').then(m => ({ default: m.CostCategoriesPage })));

// AWS Management Pages
const AWSManagementPage = lazy(() => import('../pages/AWSManagementPage').then(m => ({ default: m.AWSManagementPage })));
const AWSCostsPage = lazy(() => import('../pages/aws/AWSCostsPage').then(m => ({ default: m.AWSCostsPage })));
const AWSServicesPage = lazy(() => import('../pages/aws/AWSServicesPage').then(m => ({ default: m.AWSServicesPage })));
const AWSForecastsPage = lazy(() => import('../pages/aws/AWSForecastsPage').then(m => ({ default: m.AWSForecastsPage })));
const CostProjectorPage = lazy(() => import('../pages/aws/CostProjectorPage').then(m => ({ default: m.CostProjectorPage })));

// Payroll Pages
const PayrollDashboardPage = lazy(() => import('../pages/PayrollDashboardPage').then(m => ({ default: m.PayrollDashboardPage })));
const EmployeesPage = lazy(() => import('../pages/payroll/EmployeesPage').then(m => ({ default: m.EmployeesPage })));
const PayrollsPage = lazy(() => import('../pages/payroll/PayrollsPage').then(m => ({ default: m.PayrollsPage })));
const SalaryComponentsPage = lazy(() => import('../pages/payroll/SalaryComponentsPage').then(m => ({ default: m.SalaryComponentsPage })));

// Marketing Module
const EmailBuilderPage = lazy(() => import('../modules/marketing/pages/EmailBuilderPage').then(m => ({ default: m.EmailBuilderPage })));
const BlogListPage = lazy(() => import('../modules/marketing/pages/BlogListPage'));
const BlogEditorPage = lazy(() => import('../modules/marketing/pages/BlogEditorPage'));
const PiecesLibraryPage = lazy(() => import('../modules/marketing/pages/PiecesLibraryPage'));
const ContentCalendarPage = lazy(() => import('../modules/marketing/pages/ContentCalendarPage'));
const BlogPiecesPage = lazy(() => import('../modules/marketing/pages/BlogPiecesPage'));
const WhatsAppTemplateBuilderPage = lazy(() => import('../modules/marketing/pages/WhatsAppTemplateBuilderPage').then(m => ({ default: m.WhatsAppTemplateBuilderPage })));
const QuickReplyBuilderPage = lazy(() => import('../modules/marketing/pages/QuickReplyBuilderPage').then(m => ({ default: m.QuickReplyBuilderPage })));

// AI Agent Platform
const AIAgentsDashboardPage = lazy(() => import('../modules/ai-agents/pages/AIAgentsDashboardPage'));
const AIAgentsListPage = lazy(() => import('../modules/ai-agents/pages/AIAgentsListPage'));
const AIAgentDetailPage = lazy(() => import('../modules/ai-agents/pages/AIAgentDetailPage'));
const AISkillsPage = lazy(() => import('../modules/ai-agents/pages/AISkillsPage'));
const AISkillDetailPage = lazy(() => import('../modules/ai-agents/pages/AISkillDetailPage'));
const AISkillTemplatesPage = lazy(() => import('../modules/ai-agents/pages/AISkillTemplatesPage'));
const AIExecutionsPage = lazy(() => import('../modules/ai-agents/pages/AIExecutionsPage'));
const AICostsPage = lazy(() => import('../modules/ai-agents/pages/AICostsPage'));

// Integrations Module
const IntegrationsPage = lazy(() => import('../modules/integrations').then(m => ({ default: m.IntegrationsPage })));
const SincoProjectsPage = lazy(() => import('../modules/integrations/sinco').then(m => ({ default: m.SincoProjectsPage })));
const DaviviendaIntegrationPage = lazy(() => import('../modules/integrations/davivienda').then(m => ({ default: m.DaviviendaIntegrationPage })));
const EmailSuppressionsPage = lazy(() => import('../modules/integrations').then(m => ({ default: m.EmailSuppressionsPage })));

// CRM Migrations Module (import one-shot desde CRMs externos)
const MigrationsListPage = lazy(() => import('../modules/migrations').then(m => ({ default: m.MigrationsListPage })));
const MigrationDetailPage = lazy(() => import('../modules/migrations').then(m => ({ default: m.MigrationDetailPage })));
const NewMigrationPage = lazy(() => import('../modules/migrations').then(m => ({ default: m.NewMigrationPage })));

// Broker Portal
const BrokerDashboardPage = lazy(() => import('../modules/broker/pages/BrokerDashboardPage'));
const RegisterProspectPage = lazy(() => import('../modules/broker/pages/RegisterProspectPage'));
const BrokerCommissionsPage = lazy(() => import('../modules/broker/pages/BrokerCommissionsPage'));
// BrokerLeadsPage removed — broker leads now reuse LeadsPage with readOnly prop
// BrokerQuotesPage removed — broker quotes now reuse QuotesPage directly

// Training (Broker Portal)
const TrainingDashboardPage = lazy(() => import('../modules/training/pages/TrainingDashboardPage'));
const CourseDetailPage = lazy(() => import('../modules/training/pages/CourseDetailPage'));
const ExamPage = lazy(() => import('../modules/training/pages/ExamPage'));
const MyCertificationsHubPage = lazy(() => import('../modules/training/pages/MyCertificationsHubPage'));

// Broker Certification Flow (mirror The Deko portal: test/diploma/compliance/agreement)
const BrokerCertificationTestPage = lazy(() => import('../modules/broker/pages/BrokerCertificationTestPage'));
const BrokerCertificationCompliancePage = lazy(() => import('../modules/broker/pages/BrokerCertificationCompliancePage'));
const BrokerCertificationAgreementPage = lazy(() => import('../modules/broker/pages/BrokerCertificationAgreementPage'));
const EnvelopeSignerPage = lazy(() => import('../modules/documents/pages/EnvelopeSignerPage'));

// Training Admin
const TrainingPathsPage = lazy(() => import('../modules/training-admin/pages/TrainingPathsPage'));
const TrainingPathBuilderPage = lazy(() => import('../modules/training-admin/pages/TrainingPathBuilderPage'));
const ExamBuilderPage = lazy(() => import('../modules/training-admin/pages/ExamBuilderPage'));
const MaterialLibraryPage = lazy(() => import('../modules/training-admin/pages/MaterialLibraryPage'));
const FormTemplatesPage = lazy(() => import('../modules/training-admin/pages/FormTemplatesPage'));

// Broker Funnel & Sources
const BrokerFunnelPage = lazy(() => import('../pages/BrokerFunnelPage').then(m => ({ default: m.BrokerFunnelPage })));
const BrokerSourcesPage = lazy(() => import('../modules/broker-admin/pages/BrokerSourcesPage'));

// Broker Admin
const BrokerAdminListPage = lazy(() => import('../modules/broker-admin/pages/BrokerListPage'));
const BrokerAdminDetailPage = lazy(() => import('../modules/broker-admin/pages/BrokerDetailPage'));
const BrokerAdminOrganizationsPage = lazy(() => import('../modules/broker-admin/pages/BrokerOrganizationsPage'));
const BrokerAdminConflictsPage = lazy(() => import('../modules/broker-admin/pages/BrokerConflictsPage'));
const BrokerAdminCommissionsPage = lazy(() => import('../modules/broker-admin/pages/BrokerCommissionsAdminPage'));
const BrokerAdminApplicationsPage = lazy(() => import('../modules/broker-admin/pages/BrokerApplicationsPage'));
const BrokerApplicationReviewPage = lazy(() => import('../modules/broker-admin/pages/BrokerApplicationReviewPage'));

// Learning Admin — CertificationsHubPage consolidates approvals, compliance,
// agreement templates and signed agreements via tabs (it imports those pages
// lazily itself, so we only need to reference the hub page here).
const CertificationTemplatesPage = lazy(() => import('../modules/broker-admin/pages/CertificationTemplatesPage'));
const CertificationsHubPage = lazy(() => import('../modules/learning-admin/pages/CertificationsHubPage'));

// Tools (public SEO pages)
const ToolsIndexPage = lazy(() => import('../modules/tools/pages/ToolsIndexPage'));
const CalculadoraROIPage = lazy(() => import('../modules/tools/pages/CalculadoraROIPage'));
const CalculadoraCPLPage = lazy(() => import('../modules/tools/pages/CalculadoraCPLPage'));
const SimuladorPagosPage = lazy(() => import('../modules/tools/pages/SimuladorPagosPage'));
const ChecklistLanzamientoPage = lazy(() => import('../modules/tools/pages/ChecklistLanzamientoPage'));
const QuizCRMPage = lazy(() => import('../modules/tools/pages/QuizCRMPage'));
const BenchmarkVentasPage = lazy(() => import('../modules/tools/pages/BenchmarkVentasPage'));
const GeneradorNombrePage = lazy(() => import('../modules/tools/pages/GeneradorNombrePage'));
const ArriendoVsCompraPage = lazy(() => import('../modules/tools/pages/ArriendoVsCompraPage'));
const EdificabilidadPage = lazy(() => import('../modules/tools/pages/EdificabilidadPage'));
const FugaComercialPage = lazy(() => import('../modules/tools/pages/FugaComercialPage'));
const GeneradorWhatsAppPage = lazy(() => import('../modules/tools/pages/GeneradorWhatsAppPage'));
const AsistenteVentasPage = lazy(() => import('../modules/tools/pages/AsistenteVentasPage'));
const SimuladorAutomatizacionesPage = lazy(() => import('../modules/tools/pages/SimuladorAutomatizacionesPage'));
const GLBViewerPage = lazy(() => import('../modules/tools/pages/GLBViewerPage'));

// Pricing Structure
const PricingStructurePage = lazy(() => import('../modules/pricing-structure/pages/PricingStructurePage'));
const PricingRulesEnginePage = lazy(() => import('../modules/pricing-structure/pages/PricingRulesEnginePage'));

// OWLY Platform (SGI) - Rutas internas para equipo OWLY
const CreateCompanyPage = lazy(() => import('../modules/sgi/pages/CreateCompanyPage'));
const CompaniesListPage = lazy(() => import('../modules/sgi/pages/companies/CompaniesListPage'));
const CompanyDetailPage = lazy(() => import('../modules/sgi/pages/companies/CompanyDetailPage'));
const ProjectsListPage = lazy(() => import('../modules/sgi/pages/projects/ProjectsListPage'));
const ProjectDetailPage = lazy(() => import('../modules/sgi/pages/projects/ProjectDetailPage'));
const StageDetailPage = lazy(() => import('../modules/sgi/pages/projects/stages/StageDetailPage'));
const BlockDetailPage = lazy(() => import('../modules/sgi/pages/projects/blocks/BlockDetailPage'));
const TypologyDetailPage = lazy(() => import('../modules/sgi/pages/projects/typologies/TypologyDetailPage'));
const AmenityDetailPage = lazy(() => import('../modules/sgi/pages/projects/amenities/AmenityDetailPage'));
const PriceListDetailPage = lazy(() => import('../modules/sgi/pages/projects/price-lists/PriceListDetailPage'));
const UnitDetailPage = lazy(() => import('../modules/sgi/pages/projects/units/UnitDetailPage'));
const UnitsManagerPage = lazy(() => import('../modules/sgi/pages/units/UnitsManagerPage').then(m => ({ default: m.UnitsManagerPage })));
const ProjectKnowledgePage = lazy(() => import('../modules/project-knowledge/pages/ProjectKnowledgePage'));
const MarketsPage = lazy(() => import('../modules/sgi/pages/markets/MarketsPage'));
const MarketConfigPage = lazy(() => import('../modules/sgi/pages/markets/MarketConfigPage'));
const AvailabilityPage = lazy(() => import('../modules/sgi/pages/availability/AvailabilityPage').then(m => ({ default: m.AvailabilityPage })));
const AvailabilityUnitDetailPage = lazy(() => import('../modules/sgi/pages/availability/UnitDetailPage'));
const AmenitiesPage = lazy(() => import('../modules/sgi/pages/amenities/AmenitiesPage'));
const ComplementaryUnitsPage = lazy(() => import('../modules/sgi/pages/complementary-units/ComplementaryUnitsPage'));
const TypologiesPage = lazy(() => import('../modules/sgi/pages/typologies/TypologiesPage'));
const AssignableAreasPage = lazy(() => import('../modules/sgi/pages/assignable-areas/AssignableAreasPage'));
const MediaPage = lazy(() => import('../modules/sgi/pages/media/MediaPage'));
const UIDesignPage = lazy(() => import('../modules/design/pages/UIDesignPage'));
const SiteEditorPage = lazy(() => import('../modules/design/sites/pages/SiteEditorPage').then(m => ({ default: m.SiteEditorPage })));
const GlobalAreaTypesPage = lazy(() => import('../modules/sgi/pages/GlobalAreaTypesPage'));
const GlobalConfigPage = lazy(() => import('../modules/sgi/pages/GlobalConfigPage'));

/**
 * Configuración de rutas de la aplicación usando React Router v6
 *
 * ARQUITECTURA DE RUTAS LIMPIAS:
 * - Todas las rutas usan paths limpios sin prefijos (/owly/, /sgi/)
 * - La visibilidad se controla por permisos y tipo de usuario (platform/tenant)
 * - El sidebar filtra las secciones según el scope (platform, tenant, shared)
 *
 * Estructura de URLs:
 * - /dashboard : Dashboard principal
 * - /leads : Vista de leads
 * - /clients : Vista de clientes
 * - /projects : Gestión de proyectos
 * - /companies : Gestión de compañías (platform)
 * - /markets : Gestión de mercados
 * - etc.
 *
 * Rutas en construcción usan UnderConstructionPage
 */
export const routes: RouteObject[] = [
  // ========== RUTAS LIMPIAS (Nueva Arquitectura) ==========
  // Estas rutas no tienen prefijo de rol ni contexto
  // El contexto se maneja con ContextSelector, los permisos con usePermissions

  // --- Usuarios y Roles ---
  {
    path: '/users',
    element: <UsersListPage />,
  },
  {
    path: '/users/new',
    element: <UserCreatePage />,
  },
  {
    path: '/users/:id',
    element: <UserDetailPageModule />,
  },
  {
    path: '/users/:id/edit',
    element: <UserEditPage />,
  },
  {
    path: '/roles',
    element: <RolesPage />,
  },
  {
    path: '/roles/create',
    element: <RoleCreatePage />,
  },
  {
    path: '/permissions',
    element: <RolesPermissionsPageV2 />,
  },
  {
    path: '/permissions/matrix',
    element: <PermissionsMatrixPage />,
  },
  {
    path: '/departments',
    element: <DepartmentsPageUsers />,
  },

  // --- Compañías ---
  {
    path: '/companies',
    element: <CompaniesListPage />,
  },
  {
    path: '/companies/create',
    element: <CreateCompanyPage />,
  },
  {
    path: '/companies/:id',
    element: <CompanyDetailPage />,
  },

  // --- Mercados ---
  {
    path: '/markets',
    element: <MarketsPage />,
  },
  {
    path: '/markets/:id',
    element: <MarketConfigPage />,
  },
  {
    path: '/markets/:id/:tab',
    element: <MarketConfigPage />,
  },

  // --- Configuraciones Globales ---
  {
    path: '/global-config',
    element: <GlobalConfigPage />,
  },
  {
    path: '/global-config/:tab',
    element: <GlobalConfigPage />,
  },
  // Legacy route for backward compatibility
  {
    path: '/global-area-types',
    element: <GlobalConfigPage />,
  },

  // --- Dashboard ---
  {
    path: '/dashboard',
    element: <DashboardGeneralPage />,
  },
  {
    path: '/dashboard/agent',
    element: <AgentDashboard />,
  },
  {
    path: '/dashboard/team',
    element: <TeamDashboardModule />,
  },
  {
    path: '/dashboard/executive',
    element: <ExecutiveDashboard />,
  },
  {
    path: '/dashboard/president',
    element: <PresidentDashboard />,
  },
  {
    path: '/dashboard/business-value',
    element: <BusinessValueDashboard />,
  },
  {
    path: '/dashboard/marketing',
    element: <MarketingDashboardPage />,
  },
  {
    path: '/dashboard/marketing/campaigns/:campaignMappingId',
    element: <CampaignDetailPage />,
  },
  {
    path: '/dashboard/advisor',
    element: <AdvisorDashboardPage />,
  },
  {
    path: '/dashboard/advisor-v2',
    element: <DashboardAsesor2Page />,
  },
  {
    path: '/dashboard/panel-seguimiento',
    element: <PanelSeguimientoPage />,
  },

  // --- Owly BI (Custom Dashboards) ---
  {
    path: '/dashboard/owly-bi',
    element: <CustomDashboardsPage />,
  },
  {
    path: '/dashboard/owly-bi/:dashboardId',
    element: <CustomDashboardsPage />,
  },

  // --- OwlySales v2 Dashboards ---
  {
    path: '/dashboard/comando-central',
    element: <ComandoCentralPage />,
  },
  {
    path: '/dashboard/mis-ventas',
    element: <MisVentasPage />,
  },
  {
    path: '/dashboard/adquisicion',
    element: <AdquisicionPage />,
  },
  {
    path: '/dashboard/inventario',
    element: <InventarioPage />,
  },
  {
    path: '/dashboard/equipo-ops',
    element: <EquipoOpsPage />,
  },

  // --- Billing (Facturación) ---
  {
    path: '/billing',
    element: <UnderConstructionPage
      pageName="Overview Financiero"
      description="Revenue total, MRR y ARR de la plataforma"
      features={['Revenue total', 'MRR y ARR', 'Proyecciones financieras', 'Métodos de pago']}
    />,
  },
  {
    path: '/billing/invoices',
    element: <UnderConstructionPage
      pageName="Facturas"
      description="Gestión de facturas emitidas"
      features={['Facturas emitidas', 'Estados de pago', 'Exportación', 'Recordatorios']}
    />,
  },
  {
    path: '/billing/plans',
    element: <UnderConstructionPage
      pageName="Planes y Precios"
      description="Configuración de planes de subscripción"
      features={['Configuración de planes', 'Pricing por tier', 'Descuentos', 'Upgrades']}
    />,
  },

  // --- Analytics ---
  {
    path: '/analytics',
    element: <UnderConstructionPage
      pageName="Métricas de Negocio"
      description="KPIs y métricas del negocio"
      features={['Dashboards ejecutivos', 'Métricas de adopción', 'Análisis de uso', 'Reportes']}
    />,
  },
  {
    path: '/analytics/behavior',
    element: <UnderConstructionPage
      pageName="Comportamiento de Usuarios"
      description="Análisis de patrones de uso"
      features={['Patrones de uso', 'Feature adoption', 'User journeys', 'Heatmaps']}
    />,
  },
  {
    path: '/analytics/product',
    element: <UnderConstructionPage
      pageName="Product Analytics"
      description="Análisis de producto"
      features={['Feature usage', 'Retention', 'Cohort analysis', 'Funnel analysis']}
    />,
  },

  // --- AI Agent Platform ---
  {
    path: '/ai/dashboard',
    element: <AIAgentsDashboardPage />,
  },
  {
    path: '/ai/agents',
    element: <AIAgentsListPage />,
  },
  {
    path: '/ai/agents/:code',
    element: <AIAgentDetailPage />,
  },
  {
    path: '/ai/skills',
    element: <AISkillsPage />,
  },
  {
    path: '/ai/skills/templates',
    element: <AISkillTemplatesPage />,
  },
  {
    path: '/ai/skills/:id',
    element: <AISkillDetailPage />,
  },
  // Legacy routes — redirect old /ai/knowledge URLs to /ai/skills
  {
    path: '/ai/knowledge',
    element: <Navigate to="/ai/skills" replace />,
  },
  {
    path: '/ai/knowledge/templates',
    element: <Navigate to="/ai/skills/templates" replace />,
  },
  {
    path: '/ai/knowledge/:id',
    element: <Navigate to="/ai/skills" replace />,
  },
  {
    path: '/ai/executions',
    element: <AIExecutionsPage />,
  },
  {
    path: '/ai/costs',
    element: <AICostsPage />,
  },

  // --- Soporte ---
  {
    path: '/support',
    element: <UnderConstructionPage
      pageName="Centro de Soporte"
      description="Sistema de tickets y soporte"
      features={['Todos los tickets', 'Asignación', 'Base de conocimientos', 'Métricas']}
    />,
  },
  {
    path: '/support/open',
    element: <UnderConstructionPage
      pageName="Tickets Abiertos"
      description="Tickets pendientes de resolución"
      features={['Tickets activos', 'Priorización', 'Asignación rápida', 'Tiempos de respuesta']}
    />,
  },
  {
    path: '/support/sla',
    element: <UnderConstructionPage
      pageName="SLA Tracking"
      description="Seguimiento de SLAs"
      features={['Cumplimiento de SLA', 'Tiempos de respuesta', 'Resolución', 'Alertas']}
    />,
  },

  // --- Sistema ---
  {
    path: '/system',
    element: <UnderConstructionPage
      pageName="Configuración General"
      description="Settings de la plataforma"
      features={['Configuración general', 'Variables de entorno', 'Notificaciones', 'Logs']}
    />,
  },
  {
    path: '/system/integrations',
    element: <IntegrationsPage />,
  },
  {
    path: '/system/integrations/sinco/projects',
    element: <SincoProjectsPage />,
  },
  {
    path: '/system/integrations/davivienda',
    element: <DaviviendaIntegrationPage />,
  },
  {
    path: '/system/email-suppressions',
    element: <EmailSuppressionsPage />,
  },
  {
    path: '/system/api-keys',
    element: <ApiKeysPage />,
  },

  // --- Monitoring ---
  {
    path: '/monitoring',
    element: <MonitoringDashboardPage />,
  },
  {
    path: '/monitoring/performance',
    element: <PerformanceMonitorPage />,
  },
  {
    path: '/monitoring/errors',
    element: <ErrorMonitorPage />,
  },

  // --- Desarrollo ---
  {
    path: '/development',
    element: <UnderConstructionPage
      pageName="Roadmap"
      description="Roadmap del producto"
      features={['Roadmap', 'Sprint planning', 'Feature flags', 'Documentación']}
    />,
  },
  {
    path: '/development/releases',
    element: <UnderConstructionPage
      pageName="Releases"
      description="Gestión de releases"
      features={['Historial', 'Changelog', 'Deploy tracking', 'Rollback']}
    />,
  },
  {
    path: '/development/flags',
    element: <UnderConstructionPage
      pageName="Feature Flags"
      description="Control de feature flags"
      features={['Flags activos', 'A/B testing', 'Gradual rollouts', 'Kill switches']}
    />,
  },

  // ========== SGI PROJECT HIERARCHY ROUTES ==========
  // These routes support nested project hierarchy (stages, blocks, typologies, etc.)
  // They are needed for the SGI admin panel to navigate project structure

  // Project Knowledge — onboarding form for FAQ catalog (project-knowledge module)
  // Defined BEFORE /:id/:tab to ensure 'knowledge' is not caught as a tab
  {
    path: '/projects/:id/knowledge',
    element: <ProjectKnowledgePage />,
  },
  // Project Detail with tabs
  {
    path: '/projects/:id/:tab',
    element: <ProjectDetailPage />,
  },
  // Project Detail without tab - defaults to general
  {
    path: '/projects/:id',
    element: <ProjectDetailPage />,
  },

  // Stage Detail - Tab-based routing for stage configuration
  {
    path: '/projects/:projectId/stages/:stageId/:tab',
    element: <StageDetailPage />,
  },
  {
    path: '/projects/:projectId/stages/:stageId',
    element: <StageDetailPage />,
  },

  // Block Detail - Single view for block configuration
  {
    path: '/projects/:projectId/stages/:stageId/blocks/:blockId',
    element: <BlockDetailPage />,
  },

  // Typology Detail - Single view for typology configuration
  {
    path: '/projects/:projectId/stages/:stageId/typologies/:typologyId',
    element: <TypologyDetailPage />,
  },

  // Amenity Detail - Project level
  {
    path: '/projects/:projectId/amenities/:amenityId',
    element: <AmenityDetailPage />,
  },

  // Amenity Detail - Stage level
  {
    path: '/projects/:projectId/stages/:stageId/amenities/:amenityId',
    element: <AmenityDetailPage />,
  },

  // Amenity Detail - Block level
  {
    path: '/projects/:projectId/stages/:stageId/blocks/:blockId/amenities/:amenityId',
    element: <AmenityDetailPage />,
  },

  // Price List Detail
  {
    path: '/projects/:projectId/stages/:stageId/price-lists/:priceListId',
    element: <PriceListDetailPage />,
  },

  // Unit Detail
  {
    path: '/projects/:projectId/stages/:stageId/blocks/:blockId/units/:unitId',
    element: <UnitDetailPage />,
  },

  // Units Manager - Admin view for all units of a project
  {
    path: '/projects/:projectId/units',
    element: <UnitsManagerPage />,
  },

  // ========== TOOLS (Rutas públicas SEO) ==========
  { path: '/tools', element: <ToolsIndexPage /> },
  { path: '/tools/calculadora-roi', element: <CalculadoraROIPage /> },
  { path: '/tools/calculadora-cpl', element: <CalculadoraCPLPage /> },
  { path: '/tools/simulador-pagos', element: <SimuladorPagosPage /> },
  { path: '/tools/checklist-lanzamiento', element: <ChecklistLanzamientoPage /> },
  { path: '/tools/quiz-crm', element: <QuizCRMPage /> },
  { path: '/tools/benchmark-ventas', element: <BenchmarkVentasPage /> },
  { path: '/tools/generador-nombre', element: <GeneradorNombrePage /> },
  { path: '/tools/arriendo-vs-compra', element: <ArriendoVsCompraPage /> },
  { path: '/tools/edificabilidad', element: <EdificabilidadPage /> },
  { path: '/tools/fuga-comercial', element: <FugaComercialPage /> },
  { path: '/tools/generador-whatsapp', element: <GeneradorWhatsAppPage /> },
  { path: '/tools/asistente-ventas', element: <AsistenteVentasPage /> },
  { path: '/tools/simulador-automatizaciones', element: <SimuladorAutomatizacionesPage /> },
  { path: '/tools/glb-viewer', element: <GLBViewerPage /> },

  // ========== AUTENTICACIÓN (Rutas públicas) ==========
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
  },
  {
    path: '/reset-password',
    element: <ResetPasswordPage />,
  },
  {
    path: '/change-password',
    element: <CrmChangePasswordPage />,
  },

  // ========== PORTAL DE CLIENTES - WHITE LABEL (Compradores) ==========
  // Rutas dinámicas con companySlug para branding personalizado por empresa
  // Cada constructora tiene su portal: /portal/ocean-view/*, /portal/green-homes/*, etc.

  // Login del portal (ruta pública con branding)
  {
    path: '/portal/:companySlug/login',
    element: <PortalLoginPage />,
  },
  // Dashboard del portal (requiere autenticación de cliente)
  {
    path: '/portal/:companySlug/dashboard',
    element: <PortalApp><PortalDashboardPage /></PortalApp>,
  },
  // Propiedades del cliente
  {
    path: '/portal/:companySlug/properties',
    element: <PortalApp><PortalPropertiesPage /></PortalApp>,
  },
  {
    path: '/portal/:companySlug/properties/:id',
    element: <PortalApp><PortalPropertyDetailPage /></PortalApp>,
  },
  // Pagos del portal
  {
    path: '/portal/:companySlug/payments',
    element: <PortalApp><PortalPaymentSchedulePage /></PortalApp>,
  },
  {
    path: '/portal/:companySlug/construction',
    element: <PortalApp><UnderConstructionPage
      pageName="Avance de Obra"
      description="Sigue el progreso de construcción de tu propiedad"
      features={[
        'Timeline de construcción',
        'Galería de fotos actualizadas',
        'Hitos completados',
        'Fecha estimada de entrega'
      ]}
    /></PortalApp>,
  },
  {
    path: '/portal/:companySlug/documents',
    element: <PortalApp><UnderConstructionPage
      pageName="Mis Documentos"
      description="Accede a todos los documentos de tu propiedad"
      features={[
        'Contrato de compraventa',
        'Planos arquitectónicos',
        'Recibos de pago',
        'Certificados y licencias'
      ]}
    /></PortalApp>,
  },
  {
    path: '/portal/:companySlug/support',
    element: <PortalApp><UnderConstructionPage
      pageName="Soporte"
      description="Centro de ayuda y soporte para clientes"
      features={[
        'Crear tickets de soporte',
        'Historial de solicitudes',
        'Chat con asesor',
        'Preguntas frecuentes'
      ]}
    /></PortalApp>,
  },
  {
    path: '/portal/:companySlug/forgot-password',
    element: <PortalApp><UnderConstructionPage
      pageName="Recuperar Contraseña"
      description="Recupera el acceso a tu cuenta"
      features={[
        'Verificación por WhatsApp',
        'Código OTP',
        'Nueva contraseña segura'
      ]}
    /></PortalApp>,
  },

  // ========== PORTAL DE CLIENTES - RUTAS MULTI-TENANT (sin companySlug) ==========
  // Portal de clientes multi-tenant - el cliente ve todos sus proyectos
  {
    path: '/portal-clientes/login',
    element: <PortalLoginPage />,
  },
  {
    path: '/portal-clientes/change-password',
    element: <PortalChangePasswordPage />,
  },
  {
    path: '/portal-clientes/forgot-password',
    element: <PortalForgotPasswordPage />,
  },
  {
    path: '/portal-clientes/reset-password/:token',
    element: <PortalResetPasswordPage />,
  },
  {
    path: '/portal-clientes/dashboard',
    element: <PortalApp><PortalDashboardPage /></PortalApp>,
  },
  {
    path: '/portal-clientes/properties',
    element: <PortalApp><PortalPropertiesPage /></PortalApp>,
  },
  {
    path: '/portal-clientes/properties/:id',
    element: <PortalApp><PortalPropertyDetailPage /></PortalApp>,
  },
  {
    path: '/portal-clientes/payments',
    element: <PortalApp><PortalPaymentSchedulePage /></PortalApp>,
  },
  {
    path: '/portal-clientes',
    element: <Navigate to="/portal-clientes/login" />,
  },

  // ========== PORTAL DE CLIENTES - RUTAS LEGACY (sin companySlug) ==========
  // Mantenidas por compatibilidad con links existentes
  {
    path: '/portal/login',
    element: <PortalLoginPage />,
  },
  {
    path: '/portal/change-password',
    element: <PortalChangePasswordPage />,
  },
  {
    path: '/portal/forgot-password',
    element: <PortalForgotPasswordPage />,
  },
  {
    path: '/portal/reset-password/:token',
    element: <PortalResetPasswordPage />,
  },
  {
    path: '/portal/dashboard',
    element: <PortalApp><PortalDashboardPage /></PortalApp>,
  },
  {
    path: '/portal/properties',
    element: <PortalApp><PortalPropertiesPage /></PortalApp>,
  },
  {
    path: '/portal/properties/:id',
    element: <PortalApp><PortalPropertyDetailPage /></PortalApp>,
  },
  {
    path: '/portal/payments',
    element: <PortalApp><PortalPaymentSchedulePage /></PortalApp>,
  },
  {
    path: '/portal',
    element: <Navigate to="/portal/login" />,
  },

  {
    // Default route - smart redirect based on permissions
    path: '/',
    element: <SmartRedirect />,
  },
  // Tasks routes
  {
    path: '/tasks',
    element: <TasksPage />,
  },
  {
    path: '/advisor/tasks',
    element: <TasksPage />,
  },
  // Leads routes
  {
    path: '/leads',
    element: <LeadsPage />,
  },
  {
    path: '/leads/score-3d',
    element: <Score3DPage />,
  },
  {
    path: '/leads/:id',
    element: <LeadDetailPageWrapper />,
  },
  {
    path: '/leads/scoring/:tab?',
    element: <LeadScoringPage />,
  },
  {
    path: '/lead-scoring/:tab?',
    element: <LeadScoringPage />,
  },
  // Personas routes
  {
    path: '/personas',
    element: <PersonasPage />,
  },
  // ML Insights
  {
    path: '/ml-insights',
    element: <MLInsightsPage />,
  },
  // Clients routes
  {
    path: '/clients',
    element: <ClientsPage />,
  },
  {
    path: '/clients/:clientId',
    element: <ClientDetailPage />,
  },
  {
    path: '/dashboard/clients',
    element: <ClientDashboard />,
  },
  {
    path: '/clients/scoring',
    element: <ClientScoringPage />,
  },
  // Quotes routes
  {
    path: '/quotes',
    element: <QuotesPage />,
  },
  {
    path: '/quotes/:quoteNumber/edit',
    element: <QuoteEditPage />,
  },
  {
    path: '/quotes/settings',
    element: <QuoteSettingsPage />,
  },
  {
    path: '/quotes/settings/:tab',
    element: <QuoteSettingsPage />,
  },
  // Communication routes
  {
    path: '/calendar',
    element: <CalendarPage leads={[]} />,
  },
  {
    path: '/omnichannel',
    element: <OmnichannelPage />,
  },
  {
    path: '/omnichannel/dashboard',
    element: <OmnichannelDashboardPage />,
  },
  {
    path: '/omnichannel/call',
    element: <CallTestPage />,
  },
  {
    path: '/omnichannel/video',
    element: <VideoCallTestPage />,
  },
  {
    path: '/omnichannel/meeting',
    element: <MeetingTestPage />,
  },
  // AI & Intelligence
  {
    path: '/ai-center',
    element: <AICenterPage onNavigate={(page) => window.location.href = `/${page}`} />,
  },
  {
    path: '/ai-center/conversations',
    element: <AIConversationsPage onNavigate={() => {}} />,
  },
  {
    path: '/integrations',
    element: <IntegrationsPage />,
  },
  {
    path: '/integrations/sinco/projects',
    element: <SincoProjectsPage />,
  },
  {
    path: '/integrations/davivienda',
    element: <DaviviendaIntegrationPage />,
  },
  // CRM Migrations (import one-shot desde CRMs externos)
  {
    path: '/migrations',
    element: <MigrationsListPage />,
  },
  {
    path: '/migrations/new',
    element: <NewMigrationPage />,
  },
  {
    path: '/migrations/jobs/:id',
    element: <MigrationDetailPage />,
  },
  {
    path: '/email-suppressions',
    element: <EmailSuppressionsPage />,
  },
  {
    path: '/buyer-persona',
    element: <BuyerPersonaPage />,
  },
  // Team & Docs
  {
    path: '/team',
    element: <TeamDashboard currentProjectId={undefined} mode="schedule" />,
  },
  {
    path: '/dashboard/team-performance',
    element: <TeamDashboard currentProjectId={undefined} mode="stats" />,
  },
  {
    path: '/docs',
    element: <DocsViewer />,
  },
  // Test & Development
  {
    path: '/test',
    element: <TestPage />,
  },
  {
    path: '/virtual-scroll-demo',
    element: <VirtualScrollDemo />,
  },
  {
    path: '/badge-migration-test',
    element: <BadgeMigrationTestPage />,
  },
  {
    path: '/quote-version-demo',
    element: <QuoteVersionDemo />,
  },
  {
    path: '/docs/design-system-2.0',
    element: <DesignSystemPage />,
  },
  {
    path: '/examples/public-site',
    element: <PublicSiteDemo />,
  },
  // Rutas públicas para OrbitViewer (pueden ser embebidas o compartidas)
  {
    path: '/orbit-view/:slug',
    element: <OrbitViewPage />,
  },
  {
    path: '/public/orbit-view/:slug',
    element: <OrbitViewPage />,
  },
  // Ruta pública para scoring con voice agent (URL de un solo uso)
  {
    path: '/scoring/:token',
    element: <ScoringOrbitViewPage />,
  },
  // Ruta pública para el técnico: vista de delegación de subdominio (token UUID, 7 dias)
  {
    path: '/setup/email-domain/:token',
    element: <SetupEmailDomainPage />,
  },
  // Ruta pública para cotizaciones (sin autenticación requerida)
  {
    path: '/quotes/public/:quoteNumber',
    element: <QuotePublicPage />,
  },
  // Ruta para ver versiones específicas de cotizaciones
  {
    path: '/quotes/public/:quoteNumber/version/:versionNumber',
    element: <QuotePublicPage />,
  },
  // OrbitViewer within project context
  {
    path: '/projects/:projectSlug/orbit-view/:slug',
    element: <OrbitViewPage />,
  },
  // Showcase — next-gen project presentation (kiosk-style, multi-section).
  // Same data source as OrbitView (same slug, same auth model). Coexists with
  // /orbit-view while we iterate.
  {
    path: '/showcase/:slug',
    element: <ShowcasePage />,
  },
  {
    path: '/public/showcase/:slug',
    element: <ShowcasePage />,
  },
  {
    path: '/projects/:projectSlug/showcase/:slug',
    element: <ShowcasePage />,
  },

  // ========== TASK AUTOMATION CONFIGURATION (RESTful Architecture) ==========
  // Full URL state management - NO legacy routes

  // Lista de automatizaciones (fallback/admin view)
  {
    path: '/automations',
    element: <AutomationsListPage />,
  },

  // Smart router - navigates directly to active automation or create page
  // URL: /automations/active?type=lead|client
  {
    path: '/automations/active',
    element: <AutomationActiveRouter />,
  },

  // Create new automation (MUST be before :automationId to avoid matching "new" as ID)
  {
    path: '/automations/new',
    element: <TaskAutomationPage />,
  },

  // Automation Canvas - RESTful routes
  // STAGE_DETAIL mode: includes stageId
  {
    path: '/automations/:pipelineId/versions/:versionId/stages/:stageId',
    element: <TaskAutomationPage />,
  },
  // Automation mode: version without stage
  {
    path: '/automations/:pipelineId/versions/:versionId',
    element: <TaskAutomationPage />,
  },
  // Automation without version - loads active version
  {
    path: '/automations/:pipelineId',
    element: <TaskAutomationPage />,
  },

  // VERSIONING REMOVED - Automation Versions Page disabled
  // {
  //   path: '/automations/:pipelineId/versions',
  //   element: <PipelineVersionsPage />,
  // },
  {
    path: '/exit-reasons',
    element: <ExitReasonsPage />,
  },
  {
    path: '/transfer-reasons',
    element: <TransferReasonsPage />,
  },
  {
    path: '/lead-sources',
    element: <LeadSourcesPage />,
  },
  // Redirect old URL for bookmarks
  {
    path: '/global-config/lead-sources',
    element: <Navigate to="/lead-sources" replace />,
  },
  {
    path: '/lead-funnel',
    element: <LeadFunnelPage />,
  },
  {
    path: '/lead-playbook',
    element: <LeadPlaybookPage />,
  },
  {
    path: '/client-funnel',
    element: <ClientFunnelPage />,
  },
  // Legacy route for backward compatibility
  {
    path: '/sales-process',
    element: <LeadFunnelPage />,
  },

  // ========== PROYECTOS ==========
  {
    path: '/projects',
    element: <ProjectsListPage />,
  },

  // ========== ADMINISTRADOR DE UNIDADES (DISPONIBILIDAD) ==========
  {
    path: '/units',
    element: <AvailabilityPage />,
  },
  {
    path: '/availability',
    element: <AvailabilityPage />,
  },
  {
    path: '/availability/unit/:unitId',
    element: <AvailabilityUnitDetailPage />,
  },
  {
    path: '/complementary-units',
    element: <ComplementaryUnitsPage />,
  },
  {
    path: '/amenities',
    element: <AmenitiesPage />,
  },
  {
    path: '/typologies',
    element: <TypologiesPage />,
  },
  {
    path: '/assignable-areas',
    element: <AssignableAreasPage />,
  },
  {
    path: '/pricing',
    element: <UnderConstructionPage pageName="Pricing & Descuentos" description="Configura precios y descuentos estratégicos" features={['Gestión de precios', 'Descuentos por campaña', 'Promociones temporales', 'Simulador de financiamiento']} />,
  },
  {
    path: '/floor-plans',
    element: <UnderConstructionPage pageName="Floor Plans" description="Visualiza y gestiona planos de las unidades" features={['Biblioteca de planos', 'Visualización interactiva', 'Descarga de planos', 'Comparador de modelos']} />,
  },
  {
    path: '/gallery',
    element: <UnderConstructionPage pageName="Galería" description="Gestiona el contenido multimedia de tus proyectos" features={['Gestión de imágenes', 'Videos del proyecto', 'Recorridos virtuales', 'Organización por categorías']} />,
  },
  {
    path: '/media',
    element: <MediaPage />,
  },
  {
    path: '/diseno',
    element: <UIDesignPage />,
  },
  {
    path: '/diseno/landing',
    element: <SiteEditorPage />,
  },
  {
    path: '/sales-budget',
    element: <SalesBudgetPage />,
  },
  {
    path: '/pricing-structure',
    element: <PricingStructurePage />,
  },
  {
    path: '/pricing-structure/rules-engine',
    element: <PricingRulesEnginePage />,
  },

  // ========== CLIENTES ==========
  {
    path: '/client-churn',
    element: <ClientChurnPage />,
  },
  {
    path: '/deliveries',
    element: <UnderConstructionPage pageName="Entregas" description="Gestiona el proceso de entrega de unidades" features={['Calendario de entregas', 'Checklist de entrega', 'Documentos requeridos', 'Seguimiento de proceso']} />,
  },
  {
    path: '/billing',
    element: <UnderConstructionPage pageName="Facturación" description="Administra pagos y facturación de clientes" features={['Estados de cuenta', 'Pagos pendientes', 'Historial de pagos', 'Generación de facturas']} />,
  },
  {
    path: '/support',
    element: <UnderConstructionPage pageName="Soporte" description="Gestiona tickets y soporte postventa" features={['Sistema de tickets', 'Chat de soporte', 'Base de conocimientos', 'Historial de casos']} />,
  },

  // ========== COMUNICACIÓN ==========
  {
    path: '/dashboard/omnichannel',
    element: <OmnichannelDashboardPage />,
  },
  {
    path: '/ai-conversations',
    element: <AIConversationsPage onNavigate={() => {}} />,
  },
  // ========== CHAT INTERNO ==========
  {
    path: '/team-chat',
    element: <TeamChatPage />,
  },

  // ========== EVENTOS ==========
  {
    path: '/pending-appointments',
    element: <UnderConstructionPage pageName="Citas Pendientes" description="Gestiona todas tus citas y compromisos pendientes" features={['Lista de citas próximas', 'Recordatorios automáticos', 'Reprogramación fácil', 'Integración con calendario']} />,
  },
  {
    path: '/tasks',
    element: <UnderConstructionPage pageName="Tareas" description="Organiza y da seguimiento a tus tareas diarias" features={['Lista de tareas pendientes', 'Priorización de tareas', 'Asignación a leads/clientes', 'Recordatorios']} />,
  },

  // AI & AUTOMATIZACIÓN routes moved to /ai/* above

  // ========== EQUIPO ==========
  {
    path: '/goals',
    element: <TeamCommissionsPage />,
  },
  {
    path: '/commission-plans',
    element: <CommissionPlansPage />,
  },
  {
    path: '/advisor-commissions',
    element: <CommissionsSwitch />,
  },
  {
    path: '/leader-overrides',
    element: <LeaderOverridesPage />,
  },
  {
    path: '/commission-analytics',
    element: <CommissionAnalyticsPage />,
  },
  {
    path: '/leaderboard',
    element: <UnderConstructionPage pageName="Leaderboard" description="Tabla de clasificación del equipo" features={['Ranking en tiempo real', 'Gamificación', 'Premios y reconocimientos', 'Competencias mensuales']} />,
  },

  // ========== FINANCIAL ==========
  {
    path: '/financial',
    element: <FinancialDashboardPage />,
  },
  {
    path: '/financial/incomes',
    element: <IncomesPage />,
  },
  {
    path: '/financial/costs',
    element: <CostsPage />,
  },
  {
    path: '/financial/budgets',
    element: <BudgetsPage />,
  },
  {
    path: '/financial/income-categories',
    element: <IncomeCategoriesPage />,
  },
  {
    path: '/financial/cost-categories',
    element: <CostCategoriesPage />,
  },

  // ========== MARKETING ==========
  {
    path: '/marketing/content/blog',
    element: <BlogListPage />,
  },
  {
    path: '/marketing/content/blog/:id',
    element: <BlogEditorPage />,
  },
  {
    path: '/marketing/content/blog/:id/pieces',
    element: <BlogPiecesPage />,
  },
  {
    path: '/marketing/content/pieces',
    element: <PiecesLibraryPage />,
  },
  {
    path: '/marketing/content/calendar',
    element: <ContentCalendarPage />,
  },
  {
    path: '/marketing/email-builder',
    element: <EmailBuilderPage />,
  },
  {
    path: '/marketing/whatsapp-templates',
    element: <WhatsAppTemplateBuilderPage />,
  },
  {
    path: '/marketing/quick-replies',
    element: <QuickReplyBuilderPage />,
  },

  // ========== AWS MANAGEMENT ==========
  {
    path: '/aws-management',
    element: <AWSManagementPage />,
  },
  {
    path: '/aws-management/costs',
    element: <AWSCostsPage />,
  },
  {
    path: '/aws-management/services',
    element: <AWSServicesPage />,
  },
  {
    path: '/aws-management/forecasts',
    element: <AWSForecastsPage />,
  },
  {
    path: '/aws-management/projector',
    element: <CostProjectorPage />,
  },

  // ========== PAYROLL ==========
  {
    path: '/payroll',
    element: <PayrollDashboardPage />,
  },
  {
    path: '/payroll/employees',
    element: <EmployeesPage />,
  },
  {
    path: '/payroll/employees/new',
    element: <EmployeesPage />,
  },
  {
    path: '/payroll/payrolls',
    element: <PayrollsPage />,
  },
  {
    path: '/payroll/salary-components',
    element: <SalaryComponentsPage />,
  },

  // ========== BROKER MANAGEMENT (under team module) ==========
  {
    path: '/broker-funnel',
    element: <BrokerFunnelPage />,
  },
  {
    path: '/broker-sources',
    element: <BrokerSourcesPage />,
  },
  {
    path: '/brokers',
    element: <BrokerAdminListPage />,
  },
  {
    path: '/brokers/:id',
    element: <BrokerAdminDetailPage />,
  },
  {
    path: '/broker-organizations',
    element: <BrokerAdminOrganizationsPage />,
  },
  {
    path: '/broker-conflicts',
    element: <BrokerAdminConflictsPage />,
  },
  {
    path: '/broker-commissions',
    element: <BrokerAdminCommissionsPage />,
  },
  {
    path: '/broker-applications',
    element: <BrokerAdminApplicationsPage />,
  },
  {
    path: '/broker-applications/:id/review',
    element: <BrokerApplicationReviewPage />,
  },

  // ========== LEARNING ADMIN (new top-level module) ==========
  {
    path: '/learning/paths',
    element: <TrainingPathsPage />,
  },
  {
    path: '/learning/paths/:id',
    element: <TrainingPathBuilderPage />,
  },
  {
    path: '/learning/exams',
    element: <ExamBuilderPage />,
  },
  {
    path: '/learning/materials',
    element: <MaterialLibraryPage />,
  },
  {
    path: '/learning/forms',
    element: <FormTemplatesPage />,
  },
  {
    path: '/learning/diploma-templates',
    element: <CertificationTemplatesPage />,
  },
  {
    path: '/learning/certifications',
    element: <CertificationsHubPage />,
  },

  // ========== BACKWARD-COMPAT REDIRECTS (old /training/* and sibling URLs) ==========
  {
    path: '/training/paths',
    element: <Navigate to="/learning/paths" replace />,
  },
  {
    path: '/training/paths/:id',
    element: <LegacyTrainingPathRedirect />,
  },
  {
    path: '/training/exams',
    element: <Navigate to="/learning/exams" replace />,
  },
  {
    path: '/training/materials',
    element: <Navigate to="/learning/materials" replace />,
  },
  {
    path: '/training/certification-templates',
    element: <Navigate to="/learning/diploma-templates" replace />,
  },
  {
    path: '/training/certification-approvals',
    element: <Navigate to="/learning/certifications?tab=approvals" replace />,
  },
  {
    path: '/compliance/requirements',
    element: <Navigate to="/learning/certifications?tab=compliance" replace />,
  },
  {
    path: '/compliance/submissions',
    element: <Navigate to="/learning/certifications?tab=compliance" replace />,
  },
  {
    path: '/agreements/templates',
    element: <Navigate to="/learning/certifications?tab=templates" replace />,
  },
  {
    path: '/agreements/signatures',
    element: <Navigate to="/learning/certifications?tab=signed" replace />,
  },

  // ========== BROKER PORTAL ==========
  {
    path: '/dashboard/broker',
    element: <BrokerDashboardPage />,
  },
  // Broker-exclusive routes (portal, register, training)
  // Leads, quotes, availability, commissions use STANDARD routes with auto-detection
  {
    path: '/dashboard/broker/register',
    element: <RegisterProspectPage />,
  },
  {
    path: '/dashboard/broker/learning',
    element: <TrainingDashboardPage />,
  },
  {
    path: '/dashboard/broker/learning/:enrollmentId',
    element: <CourseDetailPage />,
  },
  {
    path: '/dashboard/broker/learning/:enrollmentId/exam/:stepId',
    element: <ExamPage />,
  },
  {
    path: '/dashboard/broker/certifications',
    element: <MyCertificationsHubPage />,
  },
  // Certification flow — 4 subroutes (mirror The Deko portal structure)
  {
    path: '/dashboard/broker/certification/test',
    element: <BrokerCertificationTestPage />,
  },
  {
    // Legacy route — "Mi Diploma" is now a tab inside /dashboard/broker/certifications
    path: '/dashboard/broker/certification/diploma',
    element: <Navigate to="/dashboard/broker/certifications?tab=diploma" replace />,
  },
  {
    path: '/dashboard/broker/certification/compliance',
    element: <BrokerCertificationCompliancePage />,
  },
  {
    path: '/dashboard/broker/certification/agreement',
    element: <BrokerCertificationAgreementPage />,
  },

  // ========== DOCUMENTS / E-SIGNATURE (apps/documents) ==========
  // Standalone signing flow — usable from any vertical (broker, client,
  // lead, etc). The broker journey step 9 will embed <EnvelopeSigner>
  // inline (Pieza #10), this page is for QA + standalone use.
  {
    path: '/documents/sign/template/:templateKey',
    element: <EnvelopeSignerPage />,
  },
  {
    path: '/documents/sign/:envelopeId',
    element: <EnvelopeSignerPage />,
  },

  // ========== DEV & TOOLS ==========
  {
    path: '/dev-tools',
    element: <UnderConstructionPage pageName="Developer Tools" description="Herramientas de desarrollo y debugging" features={['Inspector de datos', 'Logs del sistema', 'API tester', 'Performance monitor']} />,
  },
];
